/**
 * Nosto Impression Tracking Fix
 * 
 * Este script asegura que las impresiones de Nosto se registren correctamente
 * antes de que los clics puedan ser registrados, resolviendo el problema
 * de más clics que impresiones.
 * 
 * El problema ocurre cuando:
 * - Los elementos de Nosto cargan de forma asíncrona
 * - Los elementos están fuera del viewport inicial
 * - Las impresiones no se registran antes de que los clics sean posibles
 */

(function() {
  'use strict';

  // Configuración
  const CONFIG = {
    // Margen para Intersection Observer (carga antes de que sea visible)
    rootMargin: '200px',
    // Threshold mínimo para considerar un elemento visible
    threshold: 0.1,
    // Tiempo máximo de espera para que Nosto cargue (ms)
    maxWaitTime: 10000,
    // Intervalo para verificar si Nosto ha cargado (ms)
    checkInterval: 500
  };

  // Estado global
  const state = {
    observedElements: new Set(),
    impressionTracked: new Map(),
    nostoLoaded: false,
    debugMode: window.location.search.includes('nosto-debug=true') || 
               localStorage.getItem('nosto-tracking-debug') === 'true',
    stats: {
      totalElements: 0,
      impressionsTracked: 0,
      clicksBeforeImpression: 0,
      elementsObserved: 0
    }
  };

  // Exponer API de debugging globalmente
  window.NostoTrackingDebug = {
    getState: () => ({ ...state }),
    getStats: () => ({ ...state.stats }),
    getTrackedImpressions: () => Array.from(state.impressionTracked.keys()),
    getObservedElements: () => Array.from(state.observedElements),
    enableDebug: () => {
      state.debugMode = true;
      localStorage.setItem('nosto-tracking-debug', 'true');
      console.log('[Nosto Tracking] Modo debug activado');
    },
    disableDebug: () => {
      state.debugMode = false;
      localStorage.removeItem('nosto-tracking-debug');
      console.log('[Nosto Tracking] Modo debug desactivado');
    },
    reset: () => {
      state.observedElements.clear();
      state.impressionTracked.clear();
      state.stats = {
        totalElements: 0,
        impressionsTracked: 0,
        clicksBeforeImpression: 0,
        elementsObserved: 0
      };
      console.log('[Nosto Tracking] Estado reseteado');
    }
  };

  /**
   * Verifica si un elemento es realmente visible
   */
  function isElementVisible(element) {
    if (!element) return false;
    
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    
    // Verificar que no esté oculto
    if (style.display === 'none' || 
        style.visibility === 'hidden' || 
        style.opacity === '0') {
      return false;
    }
    
    // Verificar que tenga dimensiones
    if (rect.width === 0 || rect.height === 0) {
      return false;
    }
    
    // Verificar que esté en el viewport o cerca
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    
    return (
      rect.top < viewportHeight + 200 && // 200px de margen
      rect.bottom > -200 &&
      rect.left < viewportWidth + 200 &&
      rect.right > -200
    );
  }

  /**
   * Verifica si Nosto ha cargado su contenido en un elemento
   */
  function hasNostoContent(element) {
    if (!element) return false;
    
    // Verificar que el elemento tenga contenido de Nosto
    // Nosto generalmente agrega contenido dentro del div nosto_element
    const hasContent = element.children.length > 0 || 
                      element.innerHTML.trim().length > 0;
    
    // Verificar que no sea solo el div vacío
    if (hasContent) {
      // Verificar que tenga productos o contenido real
      const hasProducts = element.querySelectorAll('a, .nosto-product, [data-nosto-product-id]').length > 0;
      return hasProducts || element.textContent.trim().length > 50;
    }
    
    return false;
  }

  /**
   * Logging mejorado con modo debug
   */
  function debugLog(message, data = null) {
    if (state.debugMode) {
      if (data) {
        console.log(`[Nosto Tracking] ${message}`, data);
      } else {
        console.log(`[Nosto Tracking] ${message}`);
      }
    }
  }

  /**
   * Registra una impresión para un elemento de Nosto
   */
  function trackImpression(element, source = 'observer') {
    const elementId = element.id || element.getAttribute('data-nosto-placement');
    
    if (!elementId || state.impressionTracked.has(elementId)) {
      debugLog(`Impresión ya registrada o ID inválido para: ${elementId}`);
      return false; // Ya se registró
    }
    
    // Marcar como registrado
    state.impressionTracked.set(elementId, {
      timestamp: Date.now(),
      source: source,
      element: element
    });
    state.stats.impressionsTracked++;
    
    // Verificar visibilidad y contenido antes de registrar
    const isVisible = isElementVisible(element);
    const hasContent = hasNostoContent(element);
    
    debugLog(`Registrando impresión para: ${elementId}`, {
      visible: isVisible,
      hasContent: hasContent,
      source: source,
      children: element.children.length,
      innerHTML: element.innerHTML.length
    });
    
    // Forzar el registro de impresión de Nosto
    // Nosto usa eventos personalizados y APIs para tracking
    if (window.Nosto && window.Nosto.track) {
      try {
        // Intentar disparar el evento de impresión de Nosto
        const event = new CustomEvent('nosto:impression', {
          detail: { placement: elementId, element: element }
        });
        element.dispatchEvent(event);
        debugLog(`Evento nosto:impression disparado para: ${elementId}`);
      } catch (e) {
        console.warn('[Nosto Tracking] Error al disparar evento de Nosto:', e);
      }
    }
    
    // También disparar evento personalizado para debugging
    const customEvent = new CustomEvent('nosto:impression:tracked', {
      detail: { 
        placement: elementId, 
        element: element, 
        timestamp: Date.now(),
        source: source,
        visible: isVisible,
        hasContent: hasContent
      }
    });
    document.dispatchEvent(customEvent);
    
    // Solo mostrar log en modo debug
    debugLog(`✅ Impresión registrada para: ${elementId} (${source})`);
    return true;
  }

  /**
   * Callback para Intersection Observer
   */
  function handleIntersection(entries, observer) {
    entries.forEach(entry => {
      const element = entry.target;
      const elementId = element.id || element.getAttribute('data-nosto-placement');
      
      if (!elementId) return;
      
      // Solo registrar impresión si:
      // 1. El elemento está intersectando (visible o cerca)
      // 2. El elemento es realmente visible
      // 3. Nosto ha cargado su contenido
      // 4. No se ha registrado ya una impresión
      if (entry.isIntersecting && 
          isElementVisible(element) && 
          hasNostoContent(element) &&
          !state.impressionTracked.has(elementId)) {
        
        // Pequeño delay para asegurar que Nosto esté completamente listo
        setTimeout(() => {
          if (hasNostoContent(element) && isElementVisible(element)) {
            trackImpression(element);
          }
        }, 100);
      }
    });
  }

  /**
   * Observa un elemento de Nosto
   */
  function observeNostoElement(element) {
    if (!element) return;
    
    const elementId = element.id || element.getAttribute('data-nosto-placement');
    if (!elementId || state.observedElements.has(elementId)) {
      debugLog(`Elemento ya observado o sin ID: ${elementId}`);
      return; // Ya está siendo observado
    }
    
    state.observedElements.add(elementId);
    state.stats.elementsObserved++;
    debugLog(`Observando elemento: ${elementId}`, {
      visible: isElementVisible(element),
      hasContent: hasNostoContent(element),
      position: element.getBoundingClientRect()
    });
    
    // Crear Intersection Observer
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: CONFIG.rootMargin,
      threshold: CONFIG.threshold
    });
    
    observer.observe(element);
    
    // También verificar inmediatamente si ya es visible
    if (isElementVisible(element)) {
      debugLog(`Elemento ${elementId} ya visible, esperando contenido de Nosto...`);
      // Esperar a que Nosto cargue contenido
      let attempts = 0;
      const maxAttempts = CONFIG.maxWaitTime / CONFIG.checkInterval;
      
      const checkContent = setInterval(() => {
        attempts++;
        
        if (hasNostoContent(element)) {
          clearInterval(checkContent);
          debugLog(`Contenido de Nosto detectado para ${elementId} después de ${attempts} intentos`);
          setTimeout(() => {
            if (isElementVisible(element)) {
              trackImpression(element, 'immediate-check');
            }
          }, 100);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkContent);
          debugLog(`Timeout esperando contenido de Nosto para ${elementId}`);
        }
      }, CONFIG.checkInterval);
    }
  }

  /**
   * Encuentra y observa todos los elementos de Nosto
   */
  function findAndObserveNostoElements() {
    // Buscar elementos con clase nosto_element
    const nostoElements = document.querySelectorAll('.nosto_element[id]');
    
    debugLog(`Encontrados ${nostoElements.length} elementos .nosto_element[id]`);
    
    nostoElements.forEach(element => {
      observeNostoElement(element);
    });
    
    // También buscar elementos con atributo data-nosto-placement
    const nostoPlacements = document.querySelectorAll('[data-nosto-placement]');
    
    debugLog(`Encontrados ${nostoPlacements.length} elementos [data-nosto-placement]`);
    
    nostoPlacements.forEach(element => {
      observeNostoElement(element);
    });
    
    state.stats.totalElements = nostoElements.length + nostoPlacements.length;
  }

  /**
   * Maneja clics en elementos de Nosto para asegurar que la impresión se registró primero
   */
  function handleNostoClicks(event) {
    // Buscar el elemento de Nosto más cercano
    let target = event.target;
    let nostoElement = null;
    
    // Subir en el DOM para encontrar el contenedor de Nosto
    while (target && target !== document.body) {
      if (target.classList.contains('nosto_element') || 
          target.hasAttribute('data-nosto-placement') ||
          target.closest('.nosto_element')) {
        nostoElement = target.classList.contains('nosto_element') 
          ? target 
          : target.closest('.nosto_element');
        break;
      }
      target = target.parentElement;
    }
    
    if (nostoElement) {
      const elementId = nostoElement.id || nostoElement.getAttribute('data-nosto-placement');
      
      // Si no se ha registrado una impresión, registrarla ahora
      if (elementId && !state.impressionTracked.has(elementId)) {
        state.stats.clicksBeforeImpression++;
        // Solo mostrar warning en modo debug o si hay un problema real
        if (state.debugMode) {
          console.warn(`[Nosto Tracking] ⚠️ Clic detectado ANTES de impresión para: ${elementId}`);
          console.warn('[Nosto Tracking] Esto indica un problema - registrando impresión ahora...');
        }
        trackImpression(nostoElement, 'click-fallback');
      } else if (elementId) {
        debugLog(`Clic en elemento ${elementId} - impresión ya registrada`);
      }
    }
  }

  /**
   * Inicialización
   */
  function init() {
    // Esperar a que el DOM esté listo
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }
    
    // Buscar elementos de Nosto inmediatamente
    findAndObserveNostoElements();
    
    // Usar MutationObserver para detectar elementos de Nosto agregados dinámicamente
    const mutationObserver = new MutationObserver((mutations) => {
      let shouldCheck = false;
      
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            if (node.classList && node.classList.contains('nosto_element')) {
              shouldCheck = true;
            } else if (node.querySelector && node.querySelector('.nosto_element')) {
              shouldCheck = true;
            }
          }
        });
      });
      
      if (shouldCheck) {
        findAndObserveNostoElements();
      }
    });
    
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Escuchar clics en elementos de Nosto
    document.addEventListener('click', handleNostoClicks, true);
    
    // También verificar periódicamente (por si acaso)
    setInterval(() => {
      findAndObserveNostoElements();
    }, 2000);
    
    // Solo mostrar log de inicialización en modo debug
    debugLog('✅ Sistema de tracking de impresiones inicializado');
    
    // Mostrar estadísticas iniciales solo en modo debug
    if (state.debugMode) {
      console.log('[Nosto Tracking] Modo DEBUG activado');
      console.log('[Nosto Tracking] Para desactivar debug, ejecuta: NostoTrackingDebug.disableDebug()');
      
      // Mostrar resumen después de un breve delay
      setTimeout(() => {
        const summary = {
          elementosEncontrados: state.stats.totalElements,
          elementosObservados: state.stats.elementsObserved,
          impresionesRegistradas: state.stats.impressionsTracked,
          clicsAntesDeImpresion: state.stats.clicksBeforeImpression
        };
        console.log('[Nosto Tracking] 📊 Resumen inicial:', summary);
        
        if (state.stats.clicksBeforeImpression > 0) {
          console.warn(`[Nosto Tracking] ⚠️ Se detectaron ${state.stats.clicksBeforeImpression} clics antes de impresiones`);
        }
      }, 3000);
    }
  }

  // Inicializar cuando el script se carga
  init();
})();
